<template>
  <v-card
    title="Averages"
    variant="tonal"
    class="average-times">
    <v-card-text>
      All Loads: {{ totalAvgLoadTime }}
      <br />
      Initial Loads: {{ initialAvgLoadTime }}
    </v-card-text>
  </v-card>

  <v-card
    title="Latest"
    variant="tonal">
    <v-card-text>
      <v-table
        density="compact"
        height="300px">
        <thead>
          <tr>
            <th class="text-left">#</th>
            <th class="text-left">Time</th>
            <th class="text-left">Duration (MS)</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(result, index) in timings"
            :title="genTitle(result)"
            :key="index">
            <td>{{ index + 1 }}.</td>
            <td>{{ handleTimeStamp(result.time_stamp) }}</td>
            <td>{{ handleMs(result.time_taken_in_ms) }}</td>
          </tr>
        </tbody>
      </v-table>
    </v-card-text>
  </v-card>
</template>
<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { $http } from '../../../stores/http';

const props = defineProps(['userEditObj']);

const timings = ref([]);

// Ask the browser how it wants to format numbers based on user preferences
const locale = new Intl.NumberFormat().resolvedOptions().locale;

const logUrl = `/template_loading_logs/${props.userEditObj.uid}`;

let pollingInterval = null;

onMounted(async () => {
  timings.value = await getLogs();
  updateTimingsEvery(3000);
});

onBeforeUnmount(() => {
  if (pollingInterval) {
    clearInterval(pollingInterval);
    pollingInterval = null;
  }
});

function updateTimingsEvery(ms) {
  pollingInterval = setInterval(async () => {
    timings.value = await getLogs();
  }, ms);
}

async function getLogs() {
  const response = await $http('GET', logUrl);
  const data = JSON.parse(response);
  data.reverse();
  return data;
}

const totalAvgLoadTime = computed(() => {
  const total = timings.value.reduce((acc, curr) => {
    return acc + curr.time_taken_in_ms;
  }, 0);

  // If there aren't any loads, return N/A.
  // Stops NaN from being displayed as can't divide by 0
  if (!timings.value.length) {
    return 'N/A';
  }

  return `${handleMs(total / timings.value.length)} ms`;
});

const initialAvgLoadTime = computed(() => {
  const initialTimings = timings.value.filter(
    (result) => result.loaded_at_startup
  );

  // If there are no initial loads, return N/A.
  // Stops NaN from being displayed as can't divide by 0
  if (!initialTimings.length) {
    return 'N/A';
  }
  const total = initialTimings.reduce((acc, curr) => {
    return acc + curr.time_taken_in_ms;
  }, 0);

  return `${handleMs(total / initialTimings.length)} ms`;
});

function genTitle(result) {
  // Should display information not displayed in the table
  return `For Frame: ${result.for_frame}

Load Failed: ${result.load_failed}

Engine Behavior for this Load:
Dynamic Load/Unload: ${result.dynamic_preload}
CanvasPro Transitions: ${result.multi}${pairInfo(result)}

Loaded at Startup: ${result.loaded_at_startup}
Loaded Dynamically: ${result.loaded_for_dynamic_preload}
Loaded for Cue: ${result.loaded_for_cue_take}
Loaded as Template Upgrade: ${result.loaded_for_template_upgrade}`;
}

function pairInfo(result) {
  if (!result.multi) return '';
  const prefix = '\nTransition Pair #:';

  if (result.multi && result.loaded_for_multi) {
    return `${prefix} 2/2`;
  } else return `${prefix} 1/2`;
}

function handleTimeStamp(timeStamp) {
  const ts = new Date(timeStamp * 1000);
  return `${ts.toLocaleDateString(locale)} ${ts.toLocaleTimeString(locale)}`;
}

function handleMs(ms) {
  // round to 2 decimal places
  return ms.toFixed(2);
}
</script>
<style scoped>
.average-times {
  /* margin: 0 0 1rem 0; */
  margin-bottom: 1rem;
}
</style>
